import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useState } from "react"
import { formatISODate } from "../utils/datetime"
import Loader from "./Loader"
import Pagination from "./Pagination"
import Filter from "./TableFilters"
import IllustrationLogModal from "./IllustrationLogModal"
import ClippedText from "./ClippedText"

const columnHelper = createColumnHelper()

const SPINE_MAP = {
  "C-Spine": "CSP",
  "L-Spine": "LSP",
  "T-Spine": "TSP",
}

const IllustrationResultsTable = ({
  data,
  loading,
  onPaginationChange,
  pagination,
  pageCount,
  sorting,
  setSorting,
  filters,
  setFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [accessionNo, setAccessionNo] = useState(null)
  const [viewType, setViewType] = useState(null)
  const [inboundMessageId, setInboundMessage] = useState(null)

  const showModal = (accessionNumber, viewType, inboundMessageId) => {
    setAccessionNo(accessionNumber)
    setViewType(viewType)
    setInboundMessage(inboundMessageId)
    setIsOpen(true)
  }

  const onClose = () => {
    setIsOpen(false)
    setAccessionNo(null)
    setViewType(null)
    setInboundMessage(null)
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor("accessionNumber", {
        header: "Accession",
        id: "accessionNumber",
        cell: ({ row }) => (
          <button
            className="text-blue-500 hover:underline"
            type="button"
            onClick={() =>
              showModal(
                row.original.accessionNumber,
                row.original.viewType,
                row.original.inboundMessageId
              )
            }
          >
            {row.original.accessionNumber}
          </button>
        ),
        sortDescFirst: false,
        size: 150,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("viewType", {
        header: "View",
        id: "viewType",
        size: 80,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: ["All", "Axial", "Sagittal"],
        },
      }),
      columnHelper.accessor("spineRegion", {
        header: "Region",
        id: "spineRegion",
        size: 60,
        cell: (info) =>
          info.renderValue() ? SPINE_MAP[info.renderValue()] : "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: ["All", "C-Spine", "L-Spine", "T-Spine"],
        },
      }),
      columnHelper.accessor("curvatureType", {
        header: "Curvature",
        id: "curvatureType",
        size: 80,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: ["All", "Normal", "Straight", "Reverse"],
        },
      }),
      columnHelper.accessor("HerniationLevels", {
        header: "Herniation Levels",
        id: "HerniationLevels",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("HerniationSizes", {
        header: "Herniation Size",
        id: "HerniationSizes",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("herniationDirections", {
        header: "Herniation Directions",
        id: "herniationDirections",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("filePath", {
        header: "File Path",
        id: "filePath",
        size: 200,
        cell: (info) => (
          <ClippedText maxLength={40} text={info.renderValue() || ""} />
        ),
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: ["All", "Default"],
        },
      }),
      columnHelper.accessor("createdAt", {
        header: "Created At",
        size: 150,
        id: "createdAt",
        cell: (info) =>
          info.renderValue() ? formatISODate(info.renderValue()) : "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: [
            "All",
            "Today",
            "Yesterday",
            "This Week",
            "Last Week",
            "This Month",
            "Last Month",
          ],
        },
      }),
    ],
    []
  )

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    pageCount,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
    },
  })

  return (
    <>
      <div className="p-4">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className="p-2 border-b border-gray-200 bg-gray-100 align-top whitespace"
                    style={{ width: `${column.getSize()}px` }}
                  >
                    <div className="flex flex-col justify-start items-start">
                      <button
                        {...{
                          className: column.column.getCanSort()
                            ? "w-full select-none cursor-pointer flex items-center gap-1"
                            : "w-full",
                          onClick: column.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          column.column.columnDef.header,
                          column.getContext()
                        )}
                        {{
                          asc: <SortAscending className="h-4 w-4" />,
                          desc: <SortDescending className="h-4 w-4" />,
                        }[column.column.getIsSorted()] ?? null}
                      </button>
                      {column.column.getCanFilter() ? (
                        <div className="mt-2">
                          <Filter column={column.column} />
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className="py-2">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-100">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 border-b border-gray-200"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {!loading && data.length > 0 && <Pagination tableLib={table} />}
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No Illustration results found</div>
        )}
      </div>
      {isOpen && (
        <IllustrationLogModal
          accessionNo={accessionNo}
          inboundMessageId={inboundMessageId}
          logType={viewType}
          onClose={onClose}
        />
      )}
    </>
  )
}

export default IllustrationResultsTable
